import React, { useState, useEffect, useRef } from 'react';
import { ShoppingCart } from 'lucide-react';
import { m, AnimatePresence } from 'framer-motion';
import { cn } from "../../lib/utils";
import { useCartSummary } from "../../hooks/cart/useCartSummary";
import { t } from "../../constants/i18n";
import { SLIDE_IN_RIGHT } from "../../constants/animation";
import { useCartBootstrap } from "../../hooks/cart/useCartBootstrap";
import { useCartDrawer } from "../../hooks/cart/useCartDrawer";

// ==========================================
// CartSideWidget (UI 皮肤 - 白底黑字版)
// ==========================================

interface CartSideWidgetProps {
  className?: string;
}

export const CartSideWidget: React.FC<CartSideWidgetProps> = ({ className }) => {
  // 1. Hook 连接 (逻辑不变)
  const {
    count,
    formattedTotal,
    progress,
    isFreeShipping,
    isEmpty,
    handleOpenCart
  } = useCartSummary();

  const { isCartOpen, isLoading } = useCartDrawer();
  const [canRender, setCanRender] = useState(!isCartOpen);

  // 延迟在抽屉完全关闭后再展示，避免关闭瞬间的布局抖动影响滚动恢复
  useEffect(() => {
    if (!isCartOpen) {
      const timer = setTimeout(() => setCanRender(true), 320); // 与 drawer spring 动画接近
      return () => clearTimeout(timer);
    }
    // 抽屉打开时立刻隐藏 widget
    setCanRender(false);
  }, [isCartOpen]);

  // 0. 保底数据拉取 (逻辑不变)
  // 空车兜底拉取：使用 Hook 统一调用 Service，并限制为一次，避免请求风暴
  useCartBootstrap(isEmpty && !isLoading, { once: true });

  // 2. SVG 路径计算 (逻辑不变)
  const containerRef = useRef<HTMLDivElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const [pathData, setPathData] = useState<string>('');
  const [pathLength, setPathLength] = useState<number>(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const updatePath = () => {
      const el = containerRef.current;
      if (!el) return;
      const w = el.offsetWidth;
      const h = 52;
      const r = 26;
      const pad = 1.5;
      const d = `M ${w},${pad} L ${r},${pad} A ${r - pad},${r - pad} 0 0 0 ${r},${h - pad} L ${w},${h - pad}`;
      setPathData(d);
    };
    const observer = new ResizeObserver(() => requestAnimationFrame(updatePath));
    observer.observe(containerRef.current);
    updatePath();
    return () => observer.disconnect();
  }, [isEmpty, isCartOpen]);

  useEffect(() => {
    if (pathRef.current && pathData) {
      setPathLength(pathRef.current.getTotalLength());
    }
  }, [pathData]);

  // 3. UI 样式计算
  const strokeDashoffset = pathLength * (1 - progress);
  // 绿 vs 黄 (颜色保持，但在白底上都很清晰)
  const accentColor = isFreeShipping ? '#10B981' : '#F59E0B'; 

  // 4. 渲染
  return (
    <AnimatePresence>
      {!isEmpty && canRender && !isCartOpen && (
        <m.div
          ref={containerRef}
          onClick={handleOpenCart}
          variants={SLIDE_IN_RIGHT}
          initial="hidden"
          animate="visible"
          exit="exit"
          className={cn(
            // 基础布局
            "fixed right-0 top-1/2 z-50 flex h-[52px] -translate-y-1/2 cursor-pointer items-center rounded-l-[26px] pl-5 pr-6",
            
            // --- 🎨 样式变更区域 START ---
            // 背景：白色
            "bg-white",
            // 文字：深色 (zinc-900)
            "text-zinc-900",
            // 阴影与边框：增强阴影以在白底页面突显，使用浅色边框
            "shadow-[0_4px_20px_rgba(0,0,0,0.08)] ring-1 ring-black/5",
            // 交互：悬停变微灰 (zinc-50)
            "hover:-translate-x-1 hover:bg-zinc-50 active:scale-95",
            // --- 🎨 样式变更区域 END ---

            className
          )}
        >
          {/* --- SVG 进度条层 --- */}
          <svg className="pointer-events-none absolute left-0 top-0 h-full w-full overflow-visible">
            {/* 背景轨道：改为浅灰色 (zinc-200/zinc-100) */}
            <path
              d={pathData}
              stroke="#E4E4E7" 
              strokeWidth="3"
              fill="none"
            />
            {/* 动态进度条 */}
            <path
              ref={pathRef}
              d={pathData}
              stroke={accentColor}
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
              style={{
                strokeDasharray: pathLength,
                strokeDashoffset: strokeDashoffset,
                transition: 'stroke-dashoffset 0.8s cubic-bezier(0.22, 1, 0.36, 1)'
              }}
            />
          </svg>

{/* --- 内容层 --- */}
          <div 
            className="relative z-10 flex select-none items-center"
            // 🔧 1. 关键修复：Gap 增加到 22px
            // 之前的 16px 不够两位数角标使用，22px 提供了充足的安全距离
            style={{ gap: '22px', paddingRight: '2px' }}
          >

            {/* 图标组 */}
            <div className="relative flex items-center justify-center">
              <ShoppingCart className="h-5 w-5 text-zinc-900" />

              {/* 角标 */}
              <span
                key={count}
                // 🔧 2. 位置微调：
                // left-[12px]:稍微往回拉一点点，保持与购物车的连接感
                // -top-3: 往上提 1px (之前是 -top-2)，在垂直方向上避开文字
                className="absolute left-3 -top-1 flex h-4.5 min-w-[18px] animate-in zoom-in items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white ring-2 ring-white"
              >
                {count}
              </span>
            </div>

            {/* 文字组 */}
            <div 
              className="flex flex-col items-start justify-center"
              style={{ lineHeight: '1' }}
            >
              {/* 标签文字 */}
              <span 
                className="uppercase tracking-wider text-zinc-500"
                style={{ 
                  fontSize: '10px', 
                  fontWeight: 500,
                  marginBottom: '3px',
                  display: 'block'
                }}
              >
                {t('totalLabel')}
              </span>

              {/* 金额文字 */}
              <span 
                className="font-mono tabular-nums text-zinc-900" 
                style={{ 
                  fontSize: '15px', 
                  fontWeight: 600,
                  display: 'block',
                  marginTop: '-1px'
                }}
              >
                {formattedTotal}
              </span>
            </div>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  );
};
